import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import SectionActions from './SectionActions';
import PrintHeader from './PrintHeader';
import CantiereMap from './CantiereMap';
import {
  Building2, Plus, MapPin, Users, Edit, Trash2, Loader2,
  CheckCircle2, PauseCircle, Search, ArrowRightLeft, UserCog,
  Clock, ChevronDown, ChevronUp, Save, X, LogIn, LogOut,
  FileText, Camera, Hammer, Package, Wrench, Truck, Image,
  Undo2, Send, Euro, FolderOpen, Eye,
  Sun, Cloud, CloudRain, CloudLightning, Snowflake, CloudSun, Thermometer
} from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';

const Cantieri = () => {
  const [cantieri, setCantieri] = useState([]);
  const [operai, setOperai] = useState([]);
  const [timbrature, setTimbrature] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editCantiere, setEditCantiere] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [newCantiere, setNewCantiere] = useState({ name: '', address: '', status: 'attivo' });
  const [editCantiereData, setEditCantiereData] = useState({ name: '', address: '', status: '', latitude: '', longitude: '', geofence_radius: '200' });
  const [expandedCantiere, setExpandedCantiere] = useState(null);
  // Worker editing
  const [editingWorker, setEditingWorker] = useState(null);
  const [editWorkerData, setEditWorkerData] = useState({ role: '', cantiereId: '' });
  const [confirmDeleteWorker, setConfirmDeleteWorker] = useState(null);
  const [addingHours, setAddingHours] = useState(null);
  const [hoursData, setHoursData] = useState({ ingressoTime: '', uscitaTime: '' });
  // Giornale Lavori
  const [giornale, setGiornale] = useState([]);
  const [showAddGiornale, setShowAddGiornale] = useState(null);
  const [giornaleData, setGiornaleData] = useState({ date: '', lavorazioni: '', attrezzature: '', mezzi: '', note: '' });
  const [giornaleMagMaterials, setGiornaleMagMaterials] = useState([]);  // [{itemId, name, quantity, unit}]
  const [giornaleForMaterials, setGiornaleForMaterials] = useState([]);  // [{name, quantity, unit, bollaFattura}]
  const [editingGiornale, setEditingGiornale] = useState(null);
  const [editGData, setEditGData] = useState({ date: '', lavorazioni: '', materiali: '', attrezzature: '', mezzi: '', note: '' });
  // Foto
  const [foto, setFoto] = useState([]);
  const [showAddFoto, setShowAddFoto] = useState(null);
  const [fotoFile, setFotoFile] = useState(null);
  const [fotoDesc, setFotoDesc] = useState('');
  const [fotoDate, setFotoDate] = useState('');
  // Documenti Fiscali
  const [docFiscali, setDocFiscali] = useState([]);
  const [showAddDocFiscale, setShowAddDocFiscale] = useState(null);
  const [docFiscaleData, setDocFiscaleData] = useState({ type: 'bolla', number: '', supplier: '', description: '', date: '' });
  const [docFiscaleFile, setDocFiscaleFile] = useState(null);
  // OCR DDT
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [showScanPreview, setShowScanPreview] = useState(null);
  // Magazzino
  const [magazzino, setMagazzino] = useState([]);
  const [cantiereMateriali, setCantiereMateriali] = useState([]);
  const [cantiereTab, setCantiereTab] = useState('personale');
  // Reso/Sposta materiale
  const [showReso, setShowReso] = useState(null);
  const [resoData, setResoData] = useState({ quantity: '', action: 'reso_magazzino', destCantiereId: '' });
  const [meteoData, setMeteoData] = useState({});
  const pageRef = useRef(null);

  const loadData = async () => {
    try {
      const [c, o, t, g, f, m, cm, df] = await Promise.all([
        api.getCantieri(), api.getOperai(),
        api.getTimbratureRange(
          new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
          new Date().toISOString().split('T')[0]
        ),
        api.getGiornale(), api.getFoto(), api.getMagazzino(),
        api.getCantiereMateriali(), api.getDocFiscali()
      ]);
      setCantieri(c); setOperai(o); setTimbrature(t);
      setGiornale(g); setFoto(f); setMagazzino(m);
      setCantiereMateriali(cm); setDocFiscali(df);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  // Load weather for each cantiere
  useEffect(() => {
    if (cantieri.length === 0) return;
    cantieri.forEach(async (c) => {
      if (meteoData[c.id]) return;
      try {
        // Extract city from address
        const parts = c.address?.split(',') || [];
        const city = parts.length > 1 ? parts[parts.length - 1].trim().replace(/\(.*\)/, '').trim() : c.address?.trim() || c.name;
        const weather = await api.getMeteo(city);
        if (!weather.error) {
          setMeteoData(prev => ({ ...prev, [c.id]: weather }));
        }
      } catch {}
    });
  }, [cantieri]);

  const filtered = cantieri.filter(c =>
    !searchQuery ||
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getWorkers = (cId) => {
    // Workers assigned to this cantiere
    const assigned = operai.filter(o => o.cantiereId === cId);
    // Workers who have timbrature on this cantiere but are assigned elsewhere
    const timbWorkerIds = [...new Set(timbrature.filter(t => t.cantiereId === cId).map(t => t.workerId))];
    const timbWorkers = operai.filter(o => timbWorkerIds.includes(o.id) && o.cantiereId !== cId);
    return [...assigned, ...timbWorkers];
  };
  const getOnSite = (cId) => operai.filter(o => o.cantiereId === cId && o.status === 'on_site');
  const getInitials = (name) => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 3);

  const getWorkerHours = (workerId, forCantiereId) => {
    const wt = timbrature.filter(t => t.workerId === workerId && (!forCantiereId || t.cantiereId === forCantiereId)).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    let totalMin = 0, lastIn = null;
    for (const t of wt) {
      if (t.type === 'ingresso') lastIn = new Date(t.timestamp);
      else if (t.type === 'uscita' && lastIn) { totalMin += (new Date(t.timestamp) - lastIn) / 60000; lastIn = null; }
    }
    if (totalMin === 0) return '-';
    return `${Math.floor(totalMin / 60)}h ${Math.round(totalMin % 60)}m`;
  };

  // Cantiere CRUD
  const handleAddCantiere = async () => {
    if (!newCantiere.name || !newCantiere.address) return;
    try {
      await api.createCantiere(newCantiere);
      setNewCantiere({ name: '', address: '', status: 'attivo' });
      setShowAdd(false); loadData();
    } catch (e) { console.error(e); }
  };

  const handleEditCantiere = async () => {
    if (!editCantiereData.name || !editCantiere) return;
    try {
      await api.updateCantiere(editCantiere.id, {
        ...editCantiereData,
        latitude: parseFloat(editCantiereData.latitude) || 0,
        longitude: parseFloat(editCantiereData.longitude) || 0,
        geofence_radius: parseInt(editCantiereData.geofence_radius) || 200,
      });
      setEditCantiere(null); loadData();
    } catch (e) { console.error(e); }
  };

  const handleDeleteCantiere = async (id) => {
    try { await api.deleteCantiere(id); setConfirmDelete(null); loadData(); }
    catch (e) { console.error(e); }
  };

  const openEditCantiere = (c) => {
    setEditCantiereData({ name: c.name, address: c.address, status: c.status, latitude: String(c.latitude || ''), longitude: String(c.longitude || ''), geofence_radius: String(c.geofence_radius || '200') });
    setEditCantiere(c);
  };

  // Worker management
  const openEditWorker = (w) => {
    setEditWorkerData({ role: w.role, cantiereId: w.cantiereId });
    setEditingWorker(w);
  };

  const handleSaveWorker = async () => {
    if (!editingWorker) return;
    try {
      await api.updateOperaio(editingWorker.id, editWorkerData);
      setEditingWorker(null); loadData();
    } catch (e) { console.error(e); }
  };

  const handleDeleteWorker = async (id) => {
    try { await api.deleteOperaio(id); setConfirmDeleteWorker(null); loadData(); }
    catch (e) { console.error(e); }
  };

  const handleMoveWorker = async (workerId, newCantiereId) => {
    try { await api.updateOperaio(workerId, { cantiereId: newCantiereId }); loadData(); }
    catch (e) { console.error(e); }
  };

  // Giornale Lavori
  const handleAddGiornale = async (cantiereId) => {
    if (!giornaleData.date) return;
    try {
      await api.createGiornaleConScarico({
        cantiereId,
        date: giornaleData.date,
        lavorazioni: giornaleData.lavorazioni ? giornaleData.lavorazioni.split('\n').filter(l => l.trim()).map(l => ({ description: l.trim(), notes: '' })) : [],
        materialiMagazzino: giornaleMagMaterials.filter(m => m.itemId && m.quantity > 0),
        materialiFornitore: giornaleForMaterials.filter(m => m.name),
        attrezzature: giornaleData.attrezzature ? giornaleData.attrezzature.split('\n').filter(l => l.trim()).map(l => ({ name: l.trim(), source: 'magazzino', bollaFattura: '' })) : [],
        mezzi: giornaleData.mezzi ? giornaleData.mezzi.split('\n').filter(l => l.trim()).map(l => ({ name: l.trim(), source: 'magazzino', bollaFattura: '' })) : [],
        note: giornaleData.note,
      });
      setGiornaleData({ date: '', lavorazioni: '', attrezzature: '', mezzi: '', note: '' });
      setGiornaleMagMaterials([]); setGiornaleForMaterials([]);
      setShowAddGiornale(null); loadData();
    } catch (e) { console.error(e); }
  };

  const handleDeleteGiornale = async (id) => {
    try { await api.deleteGiornale(id); loadData(); } catch (e) { console.error(e); }
  };

  const openEditGiornale = (g) => {
    setEditGData({
      date: g.date,
      lavorazioni: (g.lavorazioni || []).map(l => l.description).join('\n'),
      materiali: (g.materiali || []).map(m => `${m.name}${m.quantity ? ' ' + m.quantity : ''}${m.unit ? ' ' + m.unit : ''}${m.bollaFattura ? ' (Bolla: ' + m.bollaFattura + ')' : ''}`).join('\n'),
      attrezzature: (g.attrezzature || []).map(a => a.name).join('\n'),
      mezzi: (g.mezzi || []).map(m => m.name).join('\n'),
      note: g.note || ''
    });
    setEditingGiornale(g);
  };

  const handleSaveGiornale = async () => {
    if (!editingGiornale) return;
    try {
      const updated = {
        date: editGData.date,
        lavorazioni: editGData.lavorazioni ? editGData.lavorazioni.split('\n').filter(l => l.trim()).map(l => ({ description: l.trim(), notes: '' })) : [],
        materiali: editGData.materiali ? editGData.materiali.split('\n').filter(l => l.trim()).map(l => ({ name: l.trim(), quantity: 0, unit: '', source: 'magazzino', bollaFattura: '' })) : [],
        attrezzature: editGData.attrezzature ? editGData.attrezzature.split('\n').filter(l => l.trim()).map(l => ({ name: l.trim(), source: '', bollaFattura: '' })) : [],
        mezzi: editGData.mezzi ? editGData.mezzi.split('\n').filter(l => l.trim()).map(l => ({ name: l.trim(), source: '', bollaFattura: '' })) : [],
        note: editGData.note
      };
      await api.updateGiornale(editingGiornale.id, updated);
      setEditingGiornale(null); loadData();
    } catch (e) { console.error(e); }
  };

  // Stampa professionale rapportino - genera PDF
  const printRapportino = async (g, cantiere) => {
    const workers = getWorkers(cantiere.id);
    const LOGO = 'https://customer-assets.emergentagent.com/job_colabella-tutorials/artifacts/wcwuz41r_LOGO.png';
    
    const printDiv = document.createElement('div');
    printDiv.style.cssText = 'position:fixed;top:0;left:0;width:794px;background:#fff;z-index:99999;font-family:Arial,Helvetica,sans-serif;color:#1f2937;padding:0;';
    
    const emptyRows = (cols, n) => Array(n).fill(0).map(() => `<tr>${Array(cols).fill('<td style="padding:7px 8px;border:1px solid #ccc;height:24px">&nbsp;</td>').join('')}</tr>`).join('');
    
    printDiv.innerHTML = `
      <div style="padding:0;margin:0">
        <!-- HEADER -->
        <div style="background:linear-gradient(135deg,#1a365d,#2563eb);padding:20px 28px;display:flex;align-items:center;justify-content:space-between">
          <div style="display:flex;align-items:center;gap:14px">
            <img src="${LOGO}" style="width:44px;height:44px;border-radius:6px" crossorigin="anonymous"/>
            <div>
              <div style="font-size:22px;font-weight:800;color:#fff;letter-spacing:0.08em">COLABELLA GROUP</div>
              <div style="font-size:10px;color:rgba(255,255,255,0.7);letter-spacing:0.15em">IMPRESA EDILE</div>
            </div>
          </div>
          <div style="text-align:right">
            <div style="font-size:20px;font-weight:700;color:#fff">Giornale dei Lavori</div>
          </div>
        </div>

        <!-- INFO BAR -->
        <table style="width:100%;border-collapse:collapse;border:1px solid #ccc">
          <tr>
            <td style="padding:8px 12px;background:#f0f0f0;font-size:12px;font-weight:600;width:180px;border:1px solid #ccc">Rapporto del:</td>
            <td style="padding:8px 12px;font-size:13px;border:1px solid #ccc">${new Date(g.date + 'T12:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</td>
          </tr>
          <tr>
            <td style="padding:8px 12px;background:#f0f0f0;font-size:12px;font-weight:600;border:1px solid #ccc">Cantiere:</td>
            <td style="padding:8px 12px;font-size:13px;border:1px solid #ccc">${cantiere.name} — ${cantiere.address || ''}</td>
          </tr>
          <tr>
            <td style="padding:8px 12px;background:#f0f0f0;font-size:12px;font-weight:600;border:1px solid #ccc">Condizioni meteo:</td>
            <td style="padding:8px 12px;font-size:13px;border:1px solid #ccc">${g.note || '—'}</td>
          </tr>
        </table>

        <!-- LAVORAZIONI ESEGUITE -->
        <div style="background:linear-gradient(90deg,#1a365d,#2563eb);color:#fff;padding:10px 16px;font-size:14px;font-weight:700;margin-top:16px;text-align:center">Lavorazioni eseguite:</div>
        <table style="width:100%;border-collapse:collapse">
          <tr style="background:#e5e7eb">
            <td style="padding:7px 12px;font-size:11px;font-weight:700;border:1px solid #ccc;width:60px">N.</td>
            <td style="padding:7px 12px;font-size:11px;font-weight:700;border:1px solid #ccc">Descrizione</td>
          </tr>
          ${(g.lavorazioni || []).map((l, i) => `<tr><td style="padding:7px 12px;border:1px solid #ccc;font-size:12px;text-align:center">${i + 1}</td><td style="padding:7px 12px;border:1px solid #ccc;font-size:12px">${l.description}</td></tr>`).join('')}
          ${emptyRows(2, Math.max(0, 4 - (g.lavorazioni?.length || 0)))}
        </table>

        <!-- PERSONALE -->
        <div style="background:linear-gradient(90deg,#1a365d,#2563eb);color:#fff;padding:10px 16px;font-size:14px;font-weight:700;margin-top:16px;text-align:center">Personale presente e manodopera:</div>
        <table style="width:100%;border-collapse:collapse">
          <tr style="background:#e5e7eb">
            <td style="padding:7px 12px;font-size:11px;font-weight:700;border:1px solid #ccc">Nominativo</td>
            <td style="padding:7px 12px;font-size:11px;font-weight:700;border:1px solid #ccc;width:140px">Qualifica</td>
            <td style="padding:7px 12px;font-size:11px;font-weight:700;border:1px solid #ccc;width:100px">Badge</td>
            <td style="padding:7px 12px;font-size:11px;font-weight:700;border:1px solid #ccc;width:100px">Ore lavorative</td>
          </tr>
          ${workers.map(w => `<tr><td style="padding:7px 12px;border:1px solid #ccc;font-size:12px;font-weight:500">${w.name}</td><td style="padding:7px 12px;border:1px solid #ccc;font-size:12px">${w.role}</td><td style="padding:7px 12px;border:1px solid #ccc;font-size:12px">${w.badge}</td><td style="padding:7px 12px;border:1px solid #ccc;font-size:12px;text-align:center">8</td></tr>`).join('')}
          ${emptyRows(4, Math.max(0, 3 - workers.length))}
        </table>

        <!-- MATERIALI -->
        <div style="background:linear-gradient(90deg,#1a365d,#2563eb);color:#fff;padding:10px 16px;font-size:14px;font-weight:700;margin-top:16px;text-align:center">Materiali:</div>
        <table style="width:100%;border-collapse:collapse">
          <tr style="background:#e5e7eb">
            <td style="padding:7px 12px;font-size:11px;font-weight:700;border:1px solid #ccc">Provenienza</td>
            <td style="padding:7px 12px;font-size:11px;font-weight:700;border:1px solid #ccc">Descrizione</td>
            <td style="padding:7px 12px;font-size:11px;font-weight:700;border:1px solid #ccc;width:100px">Quantità</td>
            <td style="padding:7px 12px;font-size:11px;font-weight:700;border:1px solid #ccc;width:120px">Bolla/Fattura</td>
          </tr>
          ${(g.materiali || []).map(m => `<tr><td style="padding:7px 12px;border:1px solid #ccc;font-size:12px">${m.source === 'magazzino' ? 'Magazzino' : 'Fornitore'}</td><td style="padding:7px 12px;border:1px solid #ccc;font-size:12px">${m.name}</td><td style="padding:7px 12px;border:1px solid #ccc;font-size:12px;text-align:center">${m.quantity || ''} ${m.unit || ''}</td><td style="padding:7px 12px;border:1px solid #ccc;font-size:12px">${m.bollaFattura || '—'}</td></tr>`).join('')}
          ${emptyRows(4, Math.max(0, 4 - (g.materiali?.length || 0)))}
        </table>

        <!-- ATTREZZATURE E MEZZI -->
        <div style="background:linear-gradient(90deg,#1a365d,#2563eb);color:#fff;padding:10px 16px;font-size:14px;font-weight:700;margin-top:16px;text-align:center">Attrezzature e mezzi d'opera:</div>
        <table style="width:100%;border-collapse:collapse">
          <tr style="background:#e5e7eb">
            <td style="padding:7px 12px;font-size:11px;font-weight:700;border:1px solid #ccc">Descrizione</td>
            <td style="padding:7px 12px;font-size:11px;font-weight:700;border:1px solid #ccc;width:140px">Proprietà</td>
            <td style="padding:7px 12px;font-size:11px;font-weight:700;border:1px solid #ccc;width:100px">Ore</td>
          </tr>
          ${(g.attrezzature || []).map(a => `<tr><td style="padding:7px 12px;border:1px solid #ccc;font-size:12px">${a.name}</td><td style="padding:7px 12px;border:1px solid #ccc;font-size:12px">Colabella Group</td><td style="padding:7px 12px;border:1px solid #ccc;font-size:12px;text-align:center">8</td></tr>`).join('')}
          ${(g.mezzi || []).map(m => `<tr><td style="padding:7px 12px;border:1px solid #ccc;font-size:12px">${m.name}</td><td style="padding:7px 12px;border:1px solid #ccc;font-size:12px">Colabella Group</td><td style="padding:7px 12px;border:1px solid #ccc;font-size:12px;text-align:center">8</td></tr>`).join('')}
          ${emptyRows(3, Math.max(0, 4 - (g.attrezzature?.length || 0) - (g.mezzi?.length || 0)))}
        </table>

        <!-- NOTE -->
        ${g.note ? `<div style="margin-top:16px;padding:12px 16px;background:#fffbeb;border:1px solid #fde68a;border-radius:4px;font-size:12px"><strong>Note:</strong> ${g.note}</div>` : ''}

        <!-- FIRME -->
        <div style="display:flex;justify-content:space-between;margin-top:30px;padding-top:16px">
          <div style="text-align:center;width:45%"><div style="border-top:1px solid #999;margin-top:50px;padding-top:6px;font-size:11px;color:#6b7280">Firma Responsabile</div></div>
          <div style="text-align:center;width:45%"><div style="border-top:1px solid #999;margin-top:50px;padding-top:6px;font-size:11px;color:#6b7280">Firma Direttore Lavori</div></div>
        </div>

        <!-- FOOTER -->
        <div style="margin-top:24px;padding-top:10px;border-top:2px solid #1a365d;font-size:9px;color:#9ca3af;text-align:center">
          <strong style="color:#1a365d">Colabella Group S.r.l.</strong> — Via Alba 1, 86046 San Martino in Pensilis (CB) — Tel: +39 320 271 1608 — info@colabella.it
        </div>
      </div>
    `;
    document.body.appendChild(printDiv);

    try {
      const { default: html2canvas } = await import('html2canvas');
      const { default: jsPDF } = await import('jspdf');
      
      const canvas = await html2canvas(printDiv, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const imgW = pdfW - 16;
      const imgH = (canvas.height / canvas.width) * imgW;
      
      if (imgH <= pdfH - 16) {
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', 8, 8, imgW, imgH);
      } else {
        let y = 0; let page = 0;
        const pageH = pdfH - 16;
        while (y < imgH) {
          if (page > 0) pdf.addPage();
          const srcY = (y / imgH) * canvas.height;
          const srcH = Math.min((pageH / imgH) * canvas.height, canvas.height - srcY);
          const dH = Math.min(pageH, imgH - y);
          const pc = document.createElement('canvas');
          pc.width = canvas.width; pc.height = srcH;
          pc.getContext('2d').drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
          pdf.addImage(pc.toDataURL('image/jpeg', 0.95), 'JPEG', 8, 8, imgW, dH);
          y += pageH; page++;
        }
      }
      pdf.save(`Giornale_Lavori_${cantiere.name}_${g.date}.pdf`);
    } catch (e) { console.error('PDF error:', e); alert('Errore generazione PDF'); }
    
    document.body.removeChild(printDiv);
  };

  // Foto
  const handleUploadFoto = async (cantiereId) => {
    if (!fotoFile) return;
    try {
      await api.uploadFoto(cantiereId, fotoFile, fotoDesc, fotoDate || new Date().toISOString().split('T')[0]);
      setFotoFile(null); setFotoDesc(''); setFotoDate('');
      setShowAddFoto(null); loadData();
    } catch (e) { console.error(e); }
  };

  const handleDeleteFoto = async (id) => {
    try { await api.deleteFoto(id); loadData(); } catch (e) { console.error(e); }
  };

  const getGiornaleForCantiere = (cId) => giornale.filter(g => g.cantiereId === cId);
  const getFotoForCantiere = (cId) => foto.filter(f => f.cantiereId === cId);
  const getMaterialiForCantiere = (cId) => cantiereMateriali.filter(m => m.cantiereId === cId);
  const getDocFiscaliForCantiere = (cId) => docFiscali.filter(d => d.cantiereId === cId);
  const getMagazzinoMateriali = () => magazzino.filter(m => m.type === 'materiale' && m.quantity > 0);

  const handleUploadDocFiscale = async (cantiereId) => {
    if (!docFiscaleFile) return;
    try {
      await api.uploadDocFiscale(cantiereId, docFiscaleData.type, docFiscaleData.number, docFiscaleData.supplier, docFiscaleData.description, docFiscaleData.date || new Date().toISOString().split('T')[0], docFiscaleFile);
      setDocFiscaleFile(null); setDocFiscaleData({ type: 'bolla', number: '', supplier: '', description: '', date: '' });
      setShowAddDocFiscale(null); loadData();
    } catch (e) { console.error(e); }
  };

  const handleDeleteDocFiscale = async (id) => {
    try { await api.deleteDocFiscale(id); loadData(); } catch (e) { console.error(e); }
  };

  const handleScanDDT = async (cantiereId, file) => {
    if (!file) return;
    setScanning(true);
    try {
      const result = await api.scanDDT(cantiereId, file);
      if (result.success && result.extracted) {
        setScanResult(result);
        setShowScanPreview(cantiereId);
      } else {
        alert('Errore nella scansione: ' + (result.error || 'Riprova'));
      }
    } catch (e) { alert('Errore: ' + e.message); }
    setScanning(false);
  };

  const handleConfirmScan = async () => {
    if (!scanResult || !showScanPreview) return;
    try {
      const ext = scanResult.extracted;
      // Add materials to giornale
      const materiali = (ext.materiali || []).map(m => ({
        name: m.nome || '', quantity: m.quantita || 0, unit: m.unita || 'pz',
        source: 'fornitore', bollaFattura: `DDT ${ext.numero_ddt || ''} - ${ext.fornitore || ''}`
      }));
      if (materiali.length > 0) {
        await api.createGiornale({
          cantiereId: showScanPreview, date: ext.data || new Date().toISOString().split('T')[0],
          lavorazioni: [{ description: `Ricezione materiali da ${ext.fornitore || 'fornitore'} (DDT ${ext.numero_ddt || ''})`, notes: '' }],
          materiali, attrezzature: [], mezzi: [], note: `Scansione automatica DDT`
        });
      }
      setScanResult(null); setShowScanPreview(null);
      loadData();
    } catch (e) { console.error(e); }
  };

  const handleReso = async () => {
    if (!showReso || !resoData.quantity) {
      alert('Inserisci la quantità');
      return;
    }
    try {
      const qty = parseFloat(resoData.quantity);
      if (resoData.action === 'reso_magazzino') {
        await api.movimentoMateriale({
          itemId: showReso.sourceItemId || showReso.id, action: 'reso_magazzino',
          quantity: qty, sourceCantiereId: showReso.cantiereId
        });
        alert(`${showReso.name}: ${qty} ${showReso.unit || 'pz'} restituiti al magazzino!`);
      } else if (resoData.action === 'sposta_cantiere' && resoData.destCantiereId) {
        await api.movimentoMateriale({
          itemId: showReso.sourceItemId || showReso.id, action: 'sposta_cantiere',
          quantity: qty, sourceCantiereId: showReso.cantiereId,
          destCantiereId: resoData.destCantiereId
        });
        alert(`${showReso.name}: ${qty} ${showReso.unit || 'pz'} spostati!`);
      }
      setShowReso(null); setResoData({ quantity: '', action: 'reso_magazzino', destCantiereId: '' });
      loadData();
    } catch (e) { alert(e.response?.data?.detail || 'Errore nello spostamento'); }
  };

  const handleChangeRole = async (workerId, newRole) => {
    try { await api.updateOperaio(workerId, { role: newRole }); loadData(); }
    catch (e) { console.error(e); }
  };

  // Manual hours entry
  const handleAddHours = async () => {
    if (!addingHours || !hoursData.ingressoTime) return;
    try {
      const today = new Date().toISOString().split('T')[0];
      await api.createTimbratura({ workerId: addingHours.id, type: 'ingresso', lat: 0, lng: 0 });
      if (hoursData.uscitaTime) {
        await api.createTimbratura({ workerId: addingHours.id, type: 'uscita', lat: 0, lng: 0 });
      }
      setAddingHours(null);
      setHoursData({ ingressoTime: '', uscitaTime: '' });
      loadData();
    } catch (e) { console.error(e); }
  };

  const handleTimbratura = async (worker, type) => {
    try { await api.createTimbratura({ workerId: worker.id, type, lat: 0, lng: 0 }); loadData(); }
    catch (e) { console.error(e); }
  };

  const totalCantieri = cantieri.length;
  const attivi = cantieri.filter(c => c.status === 'attivo').length;
  const inPausa = cantieri.filter(c => c.status !== 'attivo').length;

  if (loading) return <div className="page-container"><div className="loading-center"><Loader2 size={32} className="spin" /></div></div>;

  return (
    <div className="page-container" ref={pageRef}>
      <PrintHeader title="Gestione Cantieri" subtitle="Elenco cantieri e personale" />
      <div className="page-header">
        <div className="header-left">
          <h1 className="page-title">Gestione Cantieri</h1>
          <p className="page-subtitle">Aggiungi, modifica, elimina cantieri e gestisci il personale</p>
        </div>
        <div className="header-right">
          <SectionActions sectionTitle="Cantieri" contentRef={pageRef} />
        </div>
      </div>

      {/* Add Cantiere */}
      <div className="action-banner">
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <button className="big-action-btn"><Plus size={20} /><span>Aggiungi Nuovo Cantiere</span></button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuovo Cantiere</DialogTitle></DialogHeader>
            <div className="form-fields">
              <div className="form-field"><Label>Nome Cantiere *</Label>
                <Input value={newCantiere.name} onChange={e => setNewCantiere({...newCantiere, name: e.target.value})} placeholder="Es: Cantiere Via Garibaldi" /></div>
              <div className="form-field"><Label>Indirizzo *</Label>
                <Input value={newCantiere.address} onChange={e => setNewCantiere({...newCantiere, address: e.target.value})} placeholder="Es: Via Garibaldi 10, Torino" /></div>
              <div className="form-field"><Label>Stato</Label>
                <Select value={newCantiere.status} onValueChange={v => setNewCantiere({...newCantiere, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="attivo">Attivo</SelectItem><SelectItem value="in_pausa">In Pausa</SelectItem></SelectContent>
                </Select></div>
              <button className="form-submit" onClick={handleAddCantiere} disabled={!newCantiere.name || !newCantiere.address}>Crea Cantiere</button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="cantieri-stats">
        <div className="cstat"><Building2 size={22} color="#3b82f6" /><span className="cstat-value">{totalCantieri}</span><span className="cstat-label">Totale</span></div>
        <div className="cstat"><CheckCircle2 size={22} color="#22c55e" /><span className="cstat-value">{attivi}</span><span className="cstat-label">Attivi</span></div>
        <div className="cstat"><PauseCircle size={22} color="#f59e0b" /><span className="cstat-value">{inPausa}</span><span className="cstat-label">In Pausa</span></div>
        <div className="cstat"><Users size={22} color="#8b5cf6" /><span className="cstat-value">{operai.length}</span><span className="cstat-label">Operai</span></div>
      </div>

      {/* Search */}
      <div className="cantieri-search">
        <div className="search-bar" style={{ maxWidth: '400px' }}>
          <Search size={18} className="search-icon" />
          <Input placeholder="Cerca cantiere..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="search-input" />
        </div>
      </div>

      {/* Cantieri List */}
      <div className="cantieri-full-list">
        {filtered.map((cantiere) => {
          const workers = getWorkers(cantiere.id);
          const onSite = getOnSite(cantiere.id);
          const isExpanded = expandedCantiere === cantiere.id;

          return (
            <div key={cantiere.id} className={`cantiere-full-card ${isExpanded ? 'cantiere-expanded' : ''}`}>
              {/* Card Header */}
              <div className="cf-header" onClick={() => setExpandedCantiere(isExpanded ? null : cantiere.id)}>
                <div className="cf-icon"><Building2 size={22} /></div>
                <div className="cf-info">
                  <h3>{cantiere.name}</h3>
                  <p><MapPin size={13} /> {cantiere.address}</p>
                </div>
                <span className={`cantiere-card-status ${cantiere.status === 'attivo' ? 'cstatus-active' : 'cstatus-paused'}`}>
                  {cantiere.status === 'attivo' ? 'Attivo' : 'In Pausa'}
                </span>
                <div className="cf-meta">
                  <span className="cf-workers-count"><Users size={14} /> {workers.length} operai</span>
                  <span className="cf-onsite-count"><CheckCircle2 size={14} /> {onSite.length} presenti</span>
                  {meteoData[cantiere.id] && (() => {
                    const w = meteoData[cantiere.id];
                    const iconMap = { 'sun': Sun, 'cloud-sun': CloudSun, 'cloud': Cloud, 'cloud-rain': CloudRain, 'cloud-lightning': CloudLightning, 'snowflake': Snowflake };
                    const WeatherIcon = iconMap[w.icon] || Cloud;
                    const iconColor = w.icon === 'sun' ? '#f59e0b' : w.icon === 'cloud-rain' ? '#3b82f6' : w.icon === 'snowflake' ? '#60a5fa' : w.icon === 'cloud-lightning' ? '#8b5cf6' : '#9ca3af';
                    return (
                      <span className="cf-meteo" title={`${w.description} — ${w.city}`}>
                        <WeatherIcon size={16} color={iconColor} />
                        <span style={{ fontWeight: 700, color: '#374151' }}>{w.temperature}°</span>
                        <span style={{ fontSize: 10, color: '#9ca3af' }}>{w.description}</span>
                      </span>
                    );
                  })()}
                  {cantiere.latitude > 0 && (
                    <span className="cf-meteo" style={{ background: '#ecfdf5', borderColor: '#86efac' }} title={`Geofencing attivo — raggio ${cantiere.geofence_radius || 200}m`}>
                      <MapPin size={14} color="#22c55e" />
                      <span style={{ fontSize: 10, color: '#16a34a', fontWeight: 600 }}>GPS {cantiere.geofence_radius || 200}m</span>
                    </span>
                  )}
                </div>
                <div className="cf-actions-header">
                  <button className="cf-edit-btn" onClick={(e) => { e.stopPropagation(); openEditCantiere(cantiere); }} title="Modifica Cantiere">
                    <Edit size={16} />
                  </button>
                  <button className="cf-delete-btn" onClick={(e) => { e.stopPropagation(); setConfirmDelete(cantiere); }} title="Elimina Cantiere">
                    <Trash2 size={16} />
                  </button>
                </div>
                {isExpanded ? <ChevronUp size={20} className="cf-chevron" /> : <ChevronDown size={20} className="cf-chevron" />}
              </div>

              {/* Expanded Worker List */}
              {isExpanded && (
                <div className="cf-body">
                  <Tabs value={cantiereTab} onValueChange={setCantiereTab}>
                    <TabsList className="cf-tabslist">
                      <TabsTrigger value="personale"><Users size={14} /> Personale ({workers.length})</TabsTrigger>
                      <TabsTrigger value="materiali"><Package size={14} /> Materiali ({getMaterialiForCantiere(cantiere.id).length})</TabsTrigger>
                      <TabsTrigger value="giornale"><Hammer size={14} /> Giornale Lavori</TabsTrigger>
                      <TabsTrigger value="foto"><Camera size={14} /> Foto Cantiere</TabsTrigger>
                      <TabsTrigger value="docfiscali"><FileText size={14} /> Doc. Fiscali ({getDocFiscaliForCantiere(cantiere.id).length})</TabsTrigger>
                    </TabsList>

                    {/* Tab Personale */}
                    <TabsContent value="personale">
                      <div className="cf-workers-table">
                        <table>
                          <thead><tr><th>Operaio</th><th>Ruolo</th><th>Badge</th><th>Stato</th><th>Ore Oggi</th><th>Azioni</th></tr></thead>
                          <tbody>
                            {workers.map(w => (
                              <tr key={w.id}>
                                <td><div className="table-worker"><div className={`table-avatar ${w.status === 'on_site' ? 'avatar-online' : ''}`}>{getInitials(w.name)}</div><span>{w.name}</span></div></td>
                                <td><span className="editable-field" onClick={() => openEditWorker(w)}>{w.role} <Edit size={11} /></span></td>
                                <td><code className="badge-code-sm">{w.badge}</code></td>
                                <td><span className={`status-pill ${w.status === 'on_site' ? 'pill-green' : 'pill-red'}`}>{w.status === 'on_site' ? 'Presente' : 'Assente'}</span></td>
                                <td><span className="editable-field" onClick={() => { setAddingHours(w); }}>{getWorkerHours(w.id, cantiere.id)} <Clock size={11} /></span></td>
                                <td>
                                  <div className="cf-worker-actions">
                                    <button className="cf-wbtn cf-wbtn-in" onClick={() => handleTimbratura(w, 'ingresso')} title="Ingresso"><LogIn size={14} /></button>
                                    <button className="cf-wbtn cf-wbtn-out" onClick={() => handleTimbratura(w, 'uscita')} title="Uscita"><LogOut size={14} /></button>
                                    <Select onValueChange={(v) => handleMoveWorker(w.id, v)}>
                                      <SelectTrigger className="cf-move-select"><ArrowRightLeft size={14} /></SelectTrigger>
                                      <SelectContent>{cantieri.filter(c2 => c2.id !== cantiere.id).map(c2 => <SelectItem key={c2.id} value={c2.id}>Sposta a: {c2.name}</SelectItem>)}</SelectContent>
                                    </Select>
                                    <button className="cf-wbtn cf-wbtn-edit" onClick={() => openEditWorker(w)}><UserCog size={14} /></button>
                                    <button className="cf-wbtn cf-wbtn-del" onClick={() => setConfirmDeleteWorker(w)}><Trash2 size={14} /></button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {workers.length === 0 && <p className="empty-text">Nessun operaio</p>}
                      </div>
                    </TabsContent>

                    {/* Tab Materiali */}
                    <TabsContent value="materiali">
                      <div className="cf-materiali">
                        {(() => {
                          const mats = getMaterialiForCantiere(cantiere.id);
                          const totalValue = mats.reduce((s, m) => s + (m.prezzo_totale || 0), 0);
                          return (
                            <>
                              <div className="cf-mat-summary">
                                <span><Package size={14} /> <strong>{mats.length}</strong> materiali in cantiere</span>
                                <span><Euro size={14} /> Valore: <strong>€{totalValue.toFixed(2)}</strong></span>
                              </div>
                              <div className="cf-mat-list">
                                {mats.map(m => (
                                  <div key={m.id} className="cf-mat-row">
                                    <Package size={16} color="#3b82f6" />
                                    <span className="cf-mat-name">{m.name}</span>
                                    <span className="cf-mat-qty"><strong>{m.quantity}</strong> {m.unit}</span>
                                    <span className="cf-mat-price">€{(m.prezzo_cadauno || 0).toFixed(2)}/cad</span>
                                    <span className="cf-mat-total">€{(m.prezzo_totale || 0).toFixed(2)}</span>
                                    <div className="cf-mat-actions">
                                      <button className="cf-mat-btn cf-mat-reso" onClick={() => { setShowReso(m); setResoData({ quantity: '', action: 'reso_magazzino', destCantiereId: '' }); }} title="Reso a Magazzino">
                                        <Undo2 size={13} /> Reso
                                      </button>
                                      <button className="cf-mat-btn cf-mat-sposta" onClick={() => { setShowReso(m); setResoData({ quantity: '', action: 'sposta_cantiere', destCantiereId: '' }); }} title="Sposta a Cantiere">
                                        <Send size={13} /> Sposta
                                      </button>
                                    </div>
                                  </div>
                                ))}
                                {mats.length === 0 && <p className="empty-text">Nessun materiale assegnato. Invia materiali dal Magazzino.</p>}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </TabsContent>

                    {/* Tab Giornale Lavori */}
                    <TabsContent value="giornale">
                      <div className="cf-giornale">
                        <button className="dcb-add-btn" style={{ marginBottom: 12 }} onClick={() => { setShowAddGiornale(cantiere.id); setGiornaleData({ date: new Date().toISOString().split('T')[0], lavorazioni: '', attrezzature: '', mezzi: '', note: '' }); setGiornaleMagMaterials([]); setGiornaleForMaterials([]); }}>
                          <Plus size={14} /> Nuova Registrazione Giornaliera
                        </button>
                        {getGiornaleForCantiere(cantiere.id).map(g => (
                          <div key={g.id} className="rapp-card">
                            {/* Rapportino Header */}
                            <div className="rapp-header">
                              <div className="rapp-header-left">
                                <div className="rapp-logo-mini"><Building2 size={16} /></div>
                                <div>
                                  <span className="rapp-company">COLABELLA GROUP</span>
                                  <span className="rapp-subtitle">Rapportino di Cantiere</span>
                                </div>
                              </div>
                              <div className="rapp-header-right">
                                <span className="rapp-date-big">{new Date(g.date + 'T12:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                                <span className="rapp-cantiere-name">{cantiere.name}</span>
                              </div>
                              <div className="rapp-actions">
                                <button className="rapp-action-btn rapp-edit" onClick={() => openEditGiornale(g)} title="Modifica"><Edit size={14} /></button>
                                <button className="rapp-action-btn rapp-print" onClick={() => printRapportino(g, cantiere)} title="Scarica PDF Rapportino"><FileText size={14} /></button>
                                <button className="rapp-action-btn rapp-del" onClick={() => handleDeleteGiornale(g.id)} title="Elimina"><Trash2 size={14} /></button>
                              </div>
                            </div>

                            {/* Lavorazioni */}
                            {g.lavorazioni?.length > 0 && (
                              <div className="rapp-section">
                                <div className="rapp-section-title"><Hammer size={14} /> Lavorazioni Eseguite</div>
                                {g.lavorazioni.map((l, i) => (
                                  <div key={i} className="rapp-row">{l.description}</div>
                                ))}
                              </div>
                            )}

                            {/* Materiali con azioni */}
                            {g.materiali?.length > 0 && (
                              <div className="rapp-section">
                                <div className="rapp-section-title"><Package size={14} /> Materiali Utilizzati</div>
                                {g.materiali.map((m, i) => (
                                  <div key={i} className="rapp-mat-row">
                                    <span className="rapp-mat-source" style={{ background: m.source === 'magazzino' ? '#eff6ff' : '#fef3c7', color: m.source === 'magazzino' ? '#2563eb' : '#d97706' }}>
                                      {m.source === 'magazzino' ? 'MAG' : 'FORN'}
                                    </span>
                                    <span className="rapp-mat-name">{m.name}</span>
                                    {m.quantity > 0 && <span className="rapp-mat-qty">{m.quantity} {m.unit}</span>}
                                    {m.bollaFattura && <span className="rapp-mat-bolla">Bolla: {m.bollaFattura}</span>}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Attrezzature */}
                            {g.attrezzature?.length > 0 && (
                              <div className="rapp-section">
                                <div className="rapp-section-title"><Wrench size={14} /> Attrezzature</div>
                                {g.attrezzature.map((a, i) => <div key={i} className="rapp-row">{a.name}</div>)}
                              </div>
                            )}

                            {/* Mezzi */}
                            {g.mezzi?.length > 0 && (
                              <div className="rapp-section">
                                <div className="rapp-section-title"><Truck size={14} /> Mezzi</div>
                                {g.mezzi.map((m, i) => <div key={i} className="rapp-row">{m.name}</div>)}
                              </div>
                            )}

                            {/* Note */}
                            {g.note && <div className="rapp-note"><strong>Note:</strong> {g.note}</div>}

                            {/* Footer rapportino */}
                            <div className="rapp-footer">
                              <span>Colabella Group — {cantiere.address}</span>
                            </div>
                          </div>
                        ))}
                        {getGiornaleForCantiere(cantiere.id).length === 0 && <p className="empty-text">Nessuna registrazione. Aggiungi la prima!</p>}
                      </div>
                    </TabsContent>

                    {/* Tab Foto */}
                    <TabsContent value="foto">
                      <div className="cf-foto">
                        <button className="dcb-add-btn" style={{ marginBottom: 12 }} onClick={() => { setShowAddFoto(cantiere.id); setFotoFile(null); setFotoDesc(''); setFotoDate(new Date().toISOString().split('T')[0]); }}>
                          <Camera size={14} /> Carica Foto
                        </button>
                        <div className="foto-grid">
                          {getFotoForCantiere(cantiere.id).map(f => (
                            <div key={f.id} className="foto-card">
                              <img src={api.getUploadUrl(f.filename)} alt={f.description} className="foto-img" />
                              <div className="foto-info">
                                <span className="foto-date">{new Date(f.date + 'T12:00:00').toLocaleDateString('it-IT')}</span>
                                {f.description && <span className="foto-desc">{f.description}</span>}
                                <button className="pa-delete" onClick={() => handleDeleteFoto(f.id)}><Trash2 size={12} /></button>
                              </div>
                            </div>
                          ))}
                        </div>
                        {getFotoForCantiere(cantiere.id).length === 0 && <p className="empty-text">Nessuna foto. Carica la prima!</p>}
                      </div>
                    </TabsContent>

                    {/* Tab Documenti Fiscali */}
                    <TabsContent value="docfiscali">
                      <div className="cf-docfiscali">
                        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                          <button className="dcb-add-btn" onClick={() => { setShowAddDocFiscale(cantiere.id); setDocFiscaleData({ type: 'bolla', number: '', supplier: '', description: '', date: new Date().toISOString().split('T')[0] }); setDocFiscaleFile(null); }}>
                            <Plus size={14} /> Carica Bolla / Fattura
                          </button>
                          <label className="dcb-add-btn" style={{ color: '#8b5cf6', borderColor: '#c4b5fd', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                            {scanning ? <><Loader2 size={14} className="spin" /> Scansione AI...</> : <><Camera size={14} /> Scansiona DDT (AI)</>}
                            <input type="file" accept="image/*,.pdf,.jpg,.jpeg,.png,.heic" capture="environment" style={{ display: 'none' }} disabled={scanning} onChange={e => { if (e.target.files[0]) handleScanDDT(cantiere.id, e.target.files[0]); e.target.value = ''; }} />
                          </label>
                        </div>
                        <div className="cf-docfiscali-list">
                          {getDocFiscaliForCantiere(cantiere.id).map(d => (
                            <div key={d.id} className="cf-docf-row">
                              <FileText size={16} color={d.type === 'fattura' ? '#f59e0b' : '#3b82f6'} />
                              <span className="dcb-doc-type" style={{ background: d.type === 'fattura' ? '#fef3c7' : '#eff6ff', color: d.type === 'fattura' ? '#d97706' : '#2563eb' }}>{d.type.toUpperCase()}</span>
                              <span className="cf-docf-num">{d.number ? `N. ${d.number}` : ''}</span>
                              <span className="cf-docf-supplier">{d.supplier}</span>
                              <span className="cf-docf-date">{d.date ? new Date(d.date).toLocaleDateString('it-IT') : ''}</span>
                              <span className="cf-docf-desc">{d.description}</span>
                              {d.filename && <button className="doc-share-btn doc-btn-view" onClick={() => window.open(api.getUploadUrl(d.filename), '_blank')} title="Apri"><Eye size={13} /></button>}
                              <button className="pa-delete" onClick={() => handleDeleteDocFiscale(d.id)}><Trash2 size={13} /></button>
                            </div>
                          ))}
                          {getDocFiscaliForCantiere(cantiere.id).length === 0 && <p className="empty-text">Nessun documento fiscale. Carica bolle e fatture.</p>}
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="empty-cantieri"><Building2 size={48} color="#d1d5db" /><h3>Nessun cantiere</h3><p>Aggiungi il primo cantiere</p></div>
      )}

      {/* Edit Cantiere Dialog */}
      {editCantiere && (
        <Dialog open={!!editCantiere} onOpenChange={() => setEditCantiere(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Modifica Cantiere</DialogTitle></DialogHeader>
            <div className="form-fields">
              <div className="form-field"><Label>Nome *</Label><Input value={editCantiereData.name} onChange={e => setEditCantiereData({...editCantiereData, name: e.target.value})} /></div>
              <div className="form-field"><Label>Indirizzo *</Label><Input value={editCantiereData.address} onChange={e => setEditCantiereData({...editCantiereData, address: e.target.value})} /></div>
              <div className="form-field"><Label>Stato</Label>
                <Select value={editCantiereData.status} onValueChange={v => setEditCantiereData({...editCantiereData, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="attivo">Attivo</SelectItem><SelectItem value="in_pausa">In Pausa</SelectItem></SelectContent>
                </Select></div>
              <div style={{ background: '#eff6ff', borderRadius: 10, padding: 14, border: '1px solid #bfdbfe' }}>
                <CantiereMap
                  latitude={parseFloat(editCantiereData.latitude) || 0}
                  longitude={parseFloat(editCantiereData.longitude) || 0}
                  radius={parseInt(editCantiereData.geofence_radius) || 200}
                  onLocationChange={(lat, lng) => setEditCantiereData({...editCantiereData, latitude: String(lat), longitude: String(lng)})}
                />
                <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr', marginTop: 10 }}>
                  <div className="form-field"><Label>Latitudine</Label><Input value={editCantiereData.latitude} onChange={e => setEditCantiereData({...editCantiereData, latitude: e.target.value})} placeholder="41.6893" /></div>
                  <div className="form-field"><Label>Longitudine</Label><Input value={editCantiereData.longitude} onChange={e => setEditCantiereData({...editCantiereData, longitude: e.target.value})} placeholder="14.9890" /></div>
                  <div className="form-field"><Label>Raggio (m)</Label><Input type="number" value={editCantiereData.geofence_radius} onChange={e => setEditCantiereData({...editCantiereData, geofence_radius: e.target.value})} placeholder="200" /></div>
                </div>
              </div>
              <button className="form-submit" onClick={handleEditCantiere}>Salva Modifiche</button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Worker Role Dialog */}
      {editingWorker && (
        <Dialog open={!!editingWorker} onOpenChange={() => setEditingWorker(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Modifica {editingWorker.name}</DialogTitle></DialogHeader>
            <div className="form-fields">
              <div className="form-field"><Label>Ruolo</Label>
                <Input value={editWorkerData.role} onChange={e => setEditWorkerData({...editWorkerData, role: e.target.value})} placeholder="Es: Muratore, Elettricista" /></div>
              <div className="form-field"><Label>Cantiere</Label>
                <Select value={editWorkerData.cantiereId} onValueChange={v => setEditWorkerData({...editWorkerData, cantiereId: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{cantieri.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <button className="form-submit" onClick={handleSaveWorker}>Salva Modifiche</button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Add Hours Dialog */}
      {addingHours && (
        <Dialog open={!!addingHours} onOpenChange={() => setAddingHours(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Registra Ore — {addingHours.name}</DialogTitle></DialogHeader>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 12px' }}>
              Registra manualmente ingresso e uscita per oggi. Oppure usa i bottoni Ingresso/Uscita nella tabella.
            </p>
            <div className="form-fields">
              <div className="form-field">
                <Label>Registra Ingresso Ora</Label>
                <button className="form-submit" style={{ background: '#16a34a' }} onClick={() => { handleTimbratura(addingHours, 'ingresso'); setAddingHours(null); }}>
                  <LogIn size={16} style={{ marginRight: 6 }} /> Registra Ingresso Adesso
                </button>
              </div>
              <div className="form-field">
                <Label>Registra Uscita Ora</Label>
                <button className="form-submit" style={{ background: '#dc2626' }} onClick={() => { handleTimbratura(addingHours, 'uscita'); setAddingHours(null); }}>
                  <LogOut size={16} style={{ marginRight: 6 }} /> Registra Uscita Adesso
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Add Giornale Dialog */}
      {showAddGiornale && (
        <Dialog open={!!showAddGiornale} onOpenChange={() => setShowAddGiornale(null)}>
          <DialogContent className="max-w-lg" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <DialogHeader><DialogTitle>Registrazione Giornaliera</DialogTitle></DialogHeader>
            <div className="form-fields">
              <div className="form-field"><Label>Data *</Label><Input type="date" value={giornaleData.date} onChange={e => setGiornaleData({...giornaleData, date: e.target.value})} /></div>
              <div className="form-field"><Label>Lavorazioni Eseguite (una per riga)</Label><textarea className="form-textarea" rows={3} value={giornaleData.lavorazioni} onChange={e => setGiornaleData({...giornaleData, lavorazioni: e.target.value})} placeholder="Getto fondazioni&#10;Posa armatura" /></div>

              {/* Materiali da Magazzino */}
              <div style={{ background: '#eff6ff', borderRadius: 10, padding: 14, border: '1px solid #bfdbfe' }}>
                <p style={{ fontSize: '12px', fontWeight: 600, color: '#2563eb', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}><Package size={14} /> MATERIALI DA MAGAZZINO</p>
                {giornaleMagMaterials.map((mat, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                    <Select value={mat.itemId} onValueChange={v => {
                      const item = magazzino.find(m => m.id === v);
                      const updated = [...giornaleMagMaterials];
                      updated[i] = { ...updated[i], itemId: v, name: item?.name || '', unit: item?.unit || 'pz' };
                      setGiornaleMagMaterials(updated);
                    }}>
                      <SelectTrigger style={{ flex: 2 }}><SelectValue placeholder="Seleziona materiale" /></SelectTrigger>
                      <SelectContent>{getMagazzinoMateriali().map(m => <SelectItem key={m.id} value={m.id}>{m.name} ({m.quantity} {m.unit} disp.)</SelectItem>)}</SelectContent>
                    </Select>
                    <Input type="number" style={{ flex: 1 }} placeholder="Qtà" value={mat.quantity} onChange={e => { const u = [...giornaleMagMaterials]; u[i].quantity = e.target.value; setGiornaleMagMaterials(u); }} />
                    <button type="button" style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }} onClick={() => setGiornaleMagMaterials(giornaleMagMaterials.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
                  </div>
                ))}
                <button type="button" className="dcb-add-btn" onClick={() => setGiornaleMagMaterials([...giornaleMagMaterials, { itemId: '', name: '', quantity: '', unit: 'pz' }])}><Plus size={13} /> Da Magazzino</button>
              </div>

              {/* Materiali da Fornitore */}
              <div style={{ background: '#fef3c7', borderRadius: 10, padding: 14, border: '1px solid #fde68a' }}>
                <p style={{ fontSize: '12px', fontWeight: 600, color: '#d97706', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}><Truck size={14} /> MATERIALI DA FORNITORE (diretti)</p>
                {giornaleForMaterials.map((mat, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Input style={{ flex: 2 }} placeholder="Nome materiale" value={mat.name} onChange={e => { const u = [...giornaleForMaterials]; u[i].name = e.target.value; setGiornaleForMaterials(u); }} />
                    <Input type="number" style={{ flex: 1 }} placeholder="Qtà" value={mat.quantity} onChange={e => { const u = [...giornaleForMaterials]; u[i].quantity = e.target.value; setGiornaleForMaterials(u); }} />
                    <Input style={{ flex: 1 }} placeholder="Unità" value={mat.unit} onChange={e => { const u = [...giornaleForMaterials]; u[i].unit = e.target.value; setGiornaleForMaterials(u); }} />
                    <Input style={{ flex: 2 }} placeholder="N. Bolla/Fattura" value={mat.bollaFattura} onChange={e => { const u = [...giornaleForMaterials]; u[i].bollaFattura = e.target.value; setGiornaleForMaterials(u); }} />
                    <button type="button" style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }} onClick={() => setGiornaleForMaterials(giornaleForMaterials.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
                  </div>
                ))}
                <button type="button" className="dcb-add-btn" style={{ color: '#d97706', borderColor: '#fde68a' }} onClick={() => setGiornaleForMaterials([...giornaleForMaterials, { name: '', quantity: '', unit: 'pz', bollaFattura: '' }])}><Plus size={13} /> Da Fornitore</button>
              </div>

              <div className="form-field"><Label>Attrezzature (una per riga)</Label><textarea className="form-textarea" rows={2} value={giornaleData.attrezzature} onChange={e => setGiornaleData({...giornaleData, attrezzature: e.target.value})} placeholder="Betoniera&#10;Vibratore per cls" /></div>
              <div className="form-field"><Label>Mezzi (uno per riga)</Label><textarea className="form-textarea" rows={2} value={giornaleData.mezzi} onChange={e => setGiornaleData({...giornaleData, mezzi: e.target.value})} placeholder="Escavatore CAT 320&#10;Autobetoniera" /></div>
              <div className="form-field"><Label>Note</Label><Input value={giornaleData.note} onChange={e => setGiornaleData({...giornaleData, note: e.target.value})} placeholder="Note aggiuntive..." /></div>
              <button className="form-submit" onClick={() => handleAddGiornale(showAddGiornale)} disabled={!giornaleData.date}>Salva Registrazione</button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Giornale Dialog */}
      {editingGiornale && (
        <Dialog open={!!editingGiornale} onOpenChange={() => setEditingGiornale(null)}>
          <DialogContent className="max-w-lg" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <DialogHeader><DialogTitle>Modifica Rapportino</DialogTitle></DialogHeader>
            <div className="form-fields">
              <div className="form-field"><Label>Data</Label><Input type="date" value={editGData.date} onChange={e => setEditGData({...editGData, date: e.target.value})} /></div>
              <div className="form-field"><Label>Lavorazioni (una per riga)</Label><textarea className="form-textarea" rows={3} value={editGData.lavorazioni} onChange={e => setEditGData({...editGData, lavorazioni: e.target.value})} /></div>
              <div className="form-field"><Label>Materiali (uno per riga)</Label><textarea className="form-textarea" rows={3} value={editGData.materiali} onChange={e => setEditGData({...editGData, materiali: e.target.value})} /></div>
              <div className="form-field"><Label>Attrezzature (una per riga)</Label><textarea className="form-textarea" rows={2} value={editGData.attrezzature} onChange={e => setEditGData({...editGData, attrezzature: e.target.value})} /></div>
              <div className="form-field"><Label>Mezzi (uno per riga)</Label><textarea className="form-textarea" rows={2} value={editGData.mezzi} onChange={e => setEditGData({...editGData, mezzi: e.target.value})} /></div>
              <div className="form-field"><Label>Note</Label><Input value={editGData.note} onChange={e => setEditGData({...editGData, note: e.target.value})} /></div>
              <button className="form-submit" onClick={handleSaveGiornale}>Salva Modifiche</button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Upload Foto Dialog */}
      {showAddFoto && (
        <Dialog open={!!showAddFoto} onOpenChange={() => setShowAddFoto(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Carica Foto Cantiere</DialogTitle></DialogHeader>
            <div className="form-fields">
              <div className="form-field"><Label>Data</Label><Input type="date" value={fotoDate} onChange={e => setFotoDate(e.target.value)} /></div>
              <div className="form-field"><Label>Foto *</Label>
                <input type="file" accept="image/*" onChange={e => setFotoFile(e.target.files[0])} className="form-file-input" />
              </div>
              <div className="form-field"><Label>Descrizione</Label><Input value={fotoDesc} onChange={e => setFotoDesc(e.target.value)} placeholder="Es: Avanzamento getto piano terra" /></div>
              <button className="form-submit" onClick={() => handleUploadFoto(showAddFoto)} disabled={!fotoFile}>Carica Foto</button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Upload Documento Fiscale Dialog */}
      {showAddDocFiscale && (
        <Dialog open={!!showAddDocFiscale} onOpenChange={() => setShowAddDocFiscale(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Carica Bolla / Fattura</DialogTitle></DialogHeader>
            <div className="form-fields">
              <div className="form-row">
                <div className="form-field"><Label>Tipo *</Label>
                  <Select value={docFiscaleData.type} onValueChange={v => setDocFiscaleData({...docFiscaleData, type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="bolla">Bolla</SelectItem><SelectItem value="fattura">Fattura</SelectItem><SelectItem value="ddt">DDT</SelectItem><SelectItem value="ricevuta">Ricevuta</SelectItem></SelectContent>
                  </Select></div>
                <div className="form-field"><Label>Numero</Label><Input value={docFiscaleData.number} onChange={e => setDocFiscaleData({...docFiscaleData, number: e.target.value})} placeholder="N. documento" /></div>
              </div>
              <div className="form-field"><Label>Fornitore</Label><Input value={docFiscaleData.supplier} onChange={e => setDocFiscaleData({...docFiscaleData, supplier: e.target.value})} placeholder="Nome fornitore" /></div>
              <div className="form-field"><Label>Data</Label><Input type="date" value={docFiscaleData.date} onChange={e => setDocFiscaleData({...docFiscaleData, date: e.target.value})} /></div>
              <div className="form-field"><Label>Descrizione</Label><Input value={docFiscaleData.description} onChange={e => setDocFiscaleData({...docFiscaleData, description: e.target.value})} placeholder="Descrizione contenuto" /></div>
              <div className="form-field"><Label>Scansione/File *</Label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <label className="scan-btn scan-btn-camera" style={{ flex: 1 }}>
                    <Camera size={16} /> Scansiona con Fotocamera
                    <input type="file" accept="image/*" capture="environment" onChange={e => setDocFiscaleFile(e.target.files[0])} style={{ display: 'none' }} />
                  </label>
                  <label className="scan-btn scan-btn-file" style={{ flex: 1 }}>
                    <FolderOpen size={16} /> Scegli File
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png,.heic" onChange={e => setDocFiscaleFile(e.target.files[0])} style={{ display: 'none' }} />
                  </label>
                </div>
                {docFiscaleFile && <p style={{ fontSize: '12px', color: '#22c55e', margin: 0 }}>File selezionato: {docFiscaleFile.name}</p>}
              </div>
              <button className="form-submit" onClick={() => handleUploadDocFiscale(showAddDocFiscale)} disabled={!docFiscaleFile}>Carica Documento</button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Scan DDT Preview */}
      {showScanPreview && scanResult && (
        <Dialog open={!!showScanPreview} onOpenChange={() => { setShowScanPreview(null); setScanResult(null); }}>
          <DialogContent className="max-w-lg" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <DialogHeader><DialogTitle>DDT Scansionato — Anteprima</DialogTitle></DialogHeader>
            <div style={{ background: '#ecfdf5', border: '1px solid #86efac', borderRadius: 10, padding: 14, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckCircle2 size={20} color="#22c55e" />
              <span style={{ fontSize: 14, fontWeight: 600, color: '#16a34a' }}>AI ha letto il documento con successo!</span>
            </div>
            {(() => {
              const ext = scanResult.extracted;
              return (
                <div className="form-fields">
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <div><Label>Fornitore</Label><p style={{ fontWeight: 600, fontSize: 15 }}>{ext.fornitore || '-'}</p></div>
                    <div><Label>N. DDT</Label><p style={{ fontWeight: 600 }}>{ext.numero_ddt || '-'}</p></div>
                    <div><Label>Data</Label><p style={{ fontWeight: 600 }}>{ext.data || '-'}</p></div>
                  </div>
                  <div>
                    <Label>Materiali Estratti ({(ext.materiali || []).length})</Label>
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', marginTop: 6 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead><tr style={{ background: '#f8fafc' }}>
                          <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontSize: 11 }}>Materiale</th>
                          <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid #e5e7eb', fontSize: 11 }}>Qtà</th>
                          <th style={{ padding: '8px 10px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontSize: 11 }}>€/cad</th>
                          <th style={{ padding: '8px 10px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontSize: 11 }}>Totale</th>
                        </tr></thead>
                        <tbody>
                          {(ext.materiali || []).map((m, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                              <td style={{ padding: '6px 10px', fontWeight: 500 }}>{m.nome}</td>
                              <td style={{ padding: '6px 10px', textAlign: 'center' }}>{m.quantita} {m.unita}</td>
                              <td style={{ padding: '6px 10px', textAlign: 'right' }}>€{(m.prezzo_cadauno || 0).toFixed(2)}</td>
                              <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>€{(m.prezzo_totale || 0).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {ext.totale > 0 && <p style={{ textAlign: 'right', fontWeight: 700, fontSize: 16, marginTop: 8, color: '#1f2937' }}>Totale: €{ext.totale.toFixed(2)}</p>}
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button className="form-cancel" style={{ flex: 1 }} onClick={() => { setShowScanPreview(null); setScanResult(null); }}>Annulla</button>
                    <button className="form-submit" style={{ flex: 2 }} onClick={handleConfirmScan}>Conferma e Registra nel Cantiere</button>
                  </div>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Cantiere Confirm */}
      {confirmDelete && (
        <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Elimina Cantiere</DialogTitle></DialogHeader>
            <p style={{ color: '#6b7280', fontSize: '14px', margin: '8px 0 4px' }}>
              Eliminare <strong>{confirmDelete.name}</strong>?
            </p>
            <p style={{ color: '#ef4444', fontSize: '13px', margin: '0 0 16px' }}>
              {getWorkers(confirmDelete.id).length} operai assegnati verranno scollegati.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button className="form-cancel" onClick={() => setConfirmDelete(null)}>Annulla</button>
              <button className="form-submit-danger" onClick={() => handleDeleteCantiere(confirmDelete.id)}>Elimina</button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Worker Confirm */}
      {confirmDeleteWorker && (
        <Dialog open={!!confirmDeleteWorker} onOpenChange={() => setConfirmDeleteWorker(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Elimina Operaio</DialogTitle></DialogHeader>
            <p style={{ color: '#6b7280', fontSize: '14px', margin: '8px 0 16px' }}>
              Eliminare <strong>{confirmDeleteWorker.name}</strong> ({confirmDeleteWorker.badge})?
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button className="form-cancel" onClick={() => setConfirmDeleteWorker(null)}>Annulla</button>
              <button className="form-submit-danger" onClick={() => handleDeleteWorker(confirmDeleteWorker.id)}>Elimina</button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Reso / Sposta Materiale Dialog */}
      {showReso && (
        <Dialog open={!!showReso} onOpenChange={() => setShowReso(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>{resoData.action === 'reso_magazzino' ? 'Reso a Magazzino' : 'Sposta a Cantiere'} — {showReso.name}</DialogTitle></DialogHeader>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 12px' }}>
              Disponibili in cantiere: <strong>{showReso.quantity} {showReso.unit}</strong> (€{(showReso.prezzo_cadauno || 0).toFixed(2)}/cad)
            </p>
            <div className="form-fields">
              <div className="form-field">
                <Label>Cosa vuoi fare?</Label>
                <Select value={resoData.action} onValueChange={v => setResoData(prev => ({...prev, action: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reso_magazzino">Reso a Magazzino</SelectItem>
                    <SelectItem value="sposta_cantiere">Sposta a Altro Cantiere</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {resoData.action === 'sposta_cantiere' && (
                <div className="form-field">
                  <Label>Cantiere di destinazione *</Label>
                  <Select value={resoData.destCantiereId} onValueChange={v => setResoData(prev => ({...prev, destCantiereId: v}))}>
                    <SelectTrigger><SelectValue placeholder="Seleziona cantiere" /></SelectTrigger>
                    <SelectContent>
                      {cantieri.filter(c => c.id !== showReso.cantiereId).map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="form-field">
                <Label>Quantità da {resoData.action === 'reso_magazzino' ? 'restituire' : 'spostare'} *</Label>
                <Input type="number" step="0.5" max={showReso.quantity} value={resoData.quantity} onChange={e => setResoData(prev => ({...prev, quantity: e.target.value}))} placeholder={`Max: ${showReso.quantity}`} />
              </div>
              {resoData.quantity && (
                <p style={{ fontSize: '13px', color: '#3b82f6', margin: '0' }}>
                  Valore: €{(parseFloat(resoData.quantity || 0) * (showReso.prezzo_cadauno || 0)).toFixed(2)} — Rimanenza in cantiere: {(showReso.quantity - parseFloat(resoData.quantity || 0)).toFixed(0)} {showReso.unit}
                </p>
              )}
              <button className="form-submit" onClick={handleReso} disabled={!resoData.quantity || (resoData.action === 'sposta_cantiere' && !resoData.destCantiereId)}>
                {resoData.action === 'reso_magazzino' ? 'Restituisci a Magazzino' : 'Sposta a Cantiere'}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Cantieri;
